import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { Button, Typography } from '@mui/material';
import LayoutUserHeader from '../components/LayoutUserHeader';
import LayoutAdminHeader from '../components/LayoutAdminHeader';
import LayoutUserFooter from '../components/LayoutUserFooter';
import { useNavigate } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const ExcelUploader = () => {
  const [formattedData, setFormattedData] = useState([]);
  const [fileName, setFileName] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const gstNumber = localStorage.getItem('gstNumber');
      setIsAdmin(String(gstNumber).trim() === 'Admin');
    } catch (err) {
      console.error('Error reading gstNumber from localStorage', err);
      setIsAdmin(false);
    }
  }, []);

  /* ---------- DATE NORMALIZER (KEY FIX) ---------- */
  const normalizeDate = (value) => {
    if (!value) return null;

    // Already string
    if (typeof value === 'string') {
      // dd-mm-yyyy → yyyy-mm-dd
      const parts = value.split('-');
      if (parts.length === 3 && parts[0].length === 2) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return value;
    }

    // Excel numeric date
    if (typeof value === 'number') {
      const d = XLSX.SSF.parse_date_code(value);
      if (!d) return null;
      return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
    }

    return null;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: 'binary' });

        /* ---------- READ SHEETS WITH DATE SUPPORT ---------- */
        const sheet1Data =
          XLSX.utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[0]],
            { raw: false, dateNF: 'yyyy-MM-dd' }
          ) || [];

        const sheet2Data =
          XLSX.utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[1]],
            { raw: false, dateNF: 'yyyy-MM-dd' }
          ) || [];

        const invoicesMap = {};

        /* ---------- STEP 1: INVOICE HEADERS ---------- */
        sheet1Data.forEach((row) => {
          const key = row.InvoiceNumber;
          if (!key) return;

          if (!invoicesMap[key]) {
            invoicesMap[key] = {
              InvoiceNumber: row.InvoiceNumber,
              InvoiceType: row.InvoiceType,
              ReverseCharge: row.ReverseCharge,

              InvoiceDate: normalizeDate(row.InvoiceDate),
              DateOfSupply: normalizeDate(row.DateOfSupply),
              LrDate: normalizeDate(row.LrDate),

              StateId: row.StateId,
              StateCode: row.StateCode,
              ChallanNumber: row.ChallanNumber,
              TransportationMode: row.TransportationMode,
              VehicleNumber: row.VehicleNumber,
              PlaceOfSupply: row.PlaceOfSupply,
              TransporterId: row.TransporterId,
              TransporterName: row.TransporterName,
              LrBiltyNumber: row.LrBiltyNumber,
              GSTNumber: row.GSTNumber,

              ReceiverDetails: {
                Name: row.Receiver_Name,
                Address: row.Receiver_Address,
                GSTNumber: row.Receiver_GSTNumber,
                StateId: row.Receiver_StateId,
                StateCode: row.Receiver_StateCode,
              },

              ConsigneeDetails: {
                Name: row.Consignee_Name,
                Address: row.Consignee_Address,
                GSTNumber: row.Consignee_GSTNumber,
                StateId: row.Consignee_StateId,
                StateCode: row.Consignee_StateCode,
              },

              Products: [],
            };
          }
        });

        /* ---------- STEP 2: PRODUCTS ---------- */
        sheet2Data.forEach((row) => {
          const key = row.InvoiceNumber;
          if (!key) return;

          if (invoicesMap[key]) {
            invoicesMap[key].Products.push({
              ProductId: row.ProductId,
              HsnSacCode: row.HsnSacCode,
              Quantity: parseFloat(row.Quantity) || 0,
              UnitId: row.UnitId,
              Rate: parseFloat(row.Rate) || 0,
              TaxableValue: parseFloat(row.TaxableValue) || 0,
              IGSTPercentage: parseFloat(row.IGSTPercentage) || 0,
              CGSTPercentage: parseFloat(row.CGSTPercentage) || 0,
              SGSTPercentage: parseFloat(row.SGSTPercentage) || 0,
              IGSTAmount: parseFloat(row.IGSTAmount) || 0,
              CGSTAmount: parseFloat(row.CGSTAmount) || 0,
              SGSTAmount: parseFloat(row.SGSTAmount) || 0,
              DiscountType: row.DiscountType,
              DiscountPercentage: row.DiscountPercentage,
              DiscountAmount: row.DiscountAmount,
              OtherCharges: row.OtherCharges,
              Cess: row.Cess,
              Total: parseFloat(row.Total) || 0,
            });
          }
        });

        setFormattedData(
          Object.values(invoicesMap).filter((inv) => inv.Products.length > 0)
        );

        console.log('Formatted Data:', Object.values(invoicesMap));
      } catch (err) {
        console.error('Error reading Excel:', err);
      }
    };

    reader.readAsBinaryString(file);
  };

  const handleUpload = async () => {
    if (!formattedData.length) {
      alert('⚠️ No data to upload. Please upload an Excel file first.');
      return;
    }

    try {
      await axios.post(
        `${BASE_URL}/SalesInvoice/BulkUpload`,
        formattedData,
        { headers: { 'Content-Type': 'application/json' } }
      );

      alert('✅ Upload successful');
      navigate('/GetSaleInvoices');
    } catch (err) {
      console.error('Upload failed:', err.response || err.message);
      alert('❌ Upload failed, check console for details');
    }
  };

  return (
    <>
      {isAdmin ? <LayoutAdminHeader /> : <LayoutUserHeader />}

      <div style={{ marginTop: '6rem', textAlign: 'center' }}>
        <Typography variant="h6">Upload Sales Invoices (Excel)</Typography>
        <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />
        {fileName && <Typography variant="subtitle2">File: {fileName}</Typography>}
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          style={{ marginTop: '1rem' }}
        >
          Upload
        </Button>
      </div>
    </>
  );
};

export default ExcelUploader;
